import debounce from "lodash/debounce"
import { loadPage, observeOnce } from "./utils"
import VanillaFittty from "shared/scripts/vanilla_fittty"
import { addEventListenerWithKey } from "shared/lib/utils.js"
import { onBFCacheLoad } from "shared/scripts/back_forward_cache_utils"
import {
  saveUserSearch,
  createSearchItemFromElement,
  SearchEntryInterface,
} from "app/components/universal_search/search_history"
import { RecentSearches } from "app/components/universal_search/recent_searches"
import { SearchHelperNavigation } from "app/components/universal_search/search_helper_navigation"
import { trackSearchPerformed } from "app/utils/mixpanel_tracking"

const SEARCH_INPUT_DEBOUNCE_MS = 400

export const Search = () => {
  const { asyncSearchPath, currentView, loadingImagePath, moreAppletsForSearchPath, searchPath } =
    document.querySelector(".explore-discover-container").dataset

  const searchContainer = document.querySelector("#explore-search")
  const searchResultContainer = document.querySelector(".search-view-container")
  const searchInput = searchContainer.querySelector("input")
  const searchOffset = 32
  const minimumAppletsToLoad = 100

  let currentOffset = 32
  let historyAction = currentView !== "search" ? "pushState" : "replaceState"

  const fetchSearchResult = (searchEntry: SearchEntryInterface) => {
    saveUserSearch(searchEntry)

    document.removeEventListener("scroll", fetchSearchResultMoreApplets)

    //On the Search page we want the url bar to change to match the query and active tab.
    const parsedTerm = encodeURIComponent(searchEntry.term.toLowerCase())
    const url = `${searchPath}${parsedTerm}`
    window.history[historyAction]({ url }, "", url)

    historyAction = "replaceState"

    loadPage(`${asyncSearchPath}${parsedTerm}`).then(html => {
      currentOffset = searchOffset

      searchResultContainer.innerHTML = html
      void VanillaFittty(searchResultContainer as HTMLElement)

      fireSearchPerformed(searchEntry.term)

      document.addEventListener("scroll", fetchSearchResultMoreApplets, { once: true })
    })
  }

  const fireSearchPerformed = (term: string) => {
    const appletResultCount = searchResultContainer.querySelectorAll("[data-type='applet']").length
    const serviceResultCount = searchResultContainer.querySelectorAll("[data-type='service']").length
    trackSearchPerformed({
      term,
      appletResultCount,
      serviceResultCount,
    })
  }

  const fetchSearchResultMoreApplets = () => {
    const appletSearchContainer = document.querySelector(".explore-search")
    if (!appletSearchContainer) return

    const element = appletSearchContainer.querySelector(".explore-tile[data-sentinel='true']")
    if (!element) return

    element.dataset.sentinel = "false"

    observeOnce(element, () => {
      const searchMorePath = `${moreAppletsForSearchPath}/${encodeURIComponent(searchInput.value)}/${currentOffset}`
      const appletsFromSearch = appletSearchContainer.querySelectorAll(".explore-tile").length
      currentOffset += searchOffset

      // After achieve the minimumAppletsToLoad, don't need to ask for more
      if (appletsFromSearch < minimumAppletsToLoad) {
        loadPage(searchMorePath).then(html => {
          appletSearchContainer.insertAdjacentHTML("beforeend", html)
          void VanillaFittty(searchResultContainer as HTMLElement)
          document.addEventListener("scroll", fetchSearchResultMoreApplets, { once: true })
          window.App.Utils.logCustomDatadogAction("explore_search_completed", {
            searchTerm: searchInput.value.toLowerCase(),
          })
        })
      }
    })
  }

  const cleanSearch = () => {
    debouncedTypingSearch.cancel()
    searchResultContainer.classList.add("hide")
    searchResultContainer.innerHTML = ""

    window.dispatchEvent(new CustomEvent("exploreCleanSearch", {}))
  }

  addEventListenerWithKey(searchInput, "focus", "explore_search_focus", () => {
    window.App.Utils.logCustomDatadogAction("explore_click_search_bar")
  })

  /** Show search chrome then fetch (used after typing stabilizes or from dropdown clicks). */
  const commitSearchUiAndFetch = (searchEntry: SearchEntryInterface) => {
    window.dispatchEvent(new CustomEvent("exploreSearching", {}))

    searchResultContainer.innerHTML = `<div class="explore-search-loading-placeholder"><img class="loading" src="${loadingImagePath}" width="150" height="343" alt="" /></div>`
    searchResultContainer?.classList.remove("hide")

    if (!searchEntry.term || searchEntry.term.trim().length === 0) {
      cleanSearch()
    } else {
      fetchSearchResult(searchEntry)
    }
  }

  const debouncedTypingSearch = debounce(commitSearchUiAndFetch, SEARCH_INPUT_DEBOUNCE_MS)

  addEventListenerWithKey(searchInput, "input", "explore_search_input", event => {
    //Not trimming this here because we want the user to be able to put spaces in the search bar.
    const term = (event.target as HTMLInputElement).value

    if (!term || term.trim().length === 0) {
      debouncedTypingSearch.cancel()
      cleanSearch()
      return
    }

    debouncedTypingSearch({ type: "search", term })
  })

  addEventListenerWithKey(
    window,
    `clean-search:${searchContainer.id}`,
    `explore_search_clean_${searchContainer.id}`,
    cleanSearch
  )

  RecentSearches()
  SearchHelperNavigation(searchContainer)

  // unhide the dropdown, hidden by default to avoid FOUC
  searchContainer.querySelector(".universal-search-helper")?.removeAttribute("style")

  // We want to prevent the anchor behaviour and use our async search via search input type change.
  const dropdownHelperClick = (eventScope: "recent_searches" | "recommended_service") => (event: Event) => {
    event.preventDefault()
    event.stopPropagation()

    const serviceAnchor = (event.target as Element).closest("li > a") as HTMLAnchorElement
    const searchEntry = createSearchItemFromElement(serviceAnchor) as SearchEntryInterface

    searchInput.value = searchEntry.term
    if (eventScope === "recent_searches") {
      window.App.Utils.logCustomDatadogAction(`explore_search_recent_searches_clicked`, { term: searchEntry.term })
    } else {
      window.App.Utils.logCustomDatadogAction(`explore_search_recommended_service_clicked`, {
        service: searchEntry.term,
      })
    }
    debouncedTypingSearch.cancel()
    commitSearchUiAndFetch(searchEntry)
  }

  const recentSearchesWrapper = searchContainer.querySelector(".recent-searches")
  if (recentSearchesWrapper) {
    addEventListenerWithKey(
      recentSearchesWrapper,
      "click",
      "explore_search_recent_searches_click",
      dropdownHelperClick("recent_searches")
    )
  }

  const recommendedServicesWrapper = searchContainer.querySelector(".recommended-services")
  if (recommendedServicesWrapper) {
    addEventListenerWithKey(
      recommendedServicesWrapper,
      "click",
      "explore_search_recommended_services_click",
      dropdownHelperClick("recommended_service")
    )
  }

  if (currentView === "search") {
    document.addEventListener("scroll", fetchSearchResultMoreApplets, { once: true })
    // Fire "Search Performed" on initial page load for server-rendered search
    // (e.g. direct navigation, refresh, bookmark). Async searches fire it via
    // fetchSearchResult.
    fireSearchPerformed(searchInput.value)
  }

  onBFCacheLoad("explore_search", () => {
    Search()
  })
}
